// Badges par parcours, avantages par grade (D-100 ; maquettes 01 à 09 et m3, validées).
//  - « Mes badges » (#/badges) : un parcours par type de mission (Arbitrage, Table de marque, Buvette et cuisine…),
//    chacun son grade (En herbe, Bronze, Argent, Or, Légende) et ses étoiles ; à découvrir, coup de cœur du bureau,
//    palmarès des défis, l'échelle ; mes préférences (piliers, « Bravo à… »).
//  - Un parcours (#/badges/<parcours>) : la grande médaille, l'échelle avec la récompense de chaque grade
//    (« Utiliser »), ce qui compte, les dernières missions.
//  - Accueil Bénévole : « Ton prochain cap », relié à un vrai créneau libre ; la bande « Mes badges ».
//  - Moi : la carte « Mes badges ». La fête du passage de grade, une fois, à l'ouverture.
//  - Bureau (Bénévolat › Badges) : nouveaux grades à féliciter, « Bravo à… » du lundi, piliers en missions, donner
//    une étoile, un badge coup de cœur, les présences. Les réglages (parcours, récompenses des grades) sont dans
//    Club › Bénévolat (D-127, src/ui/reglages.ts ; #/benevoles/parcours y mène).
//  - D-127 : les parcours du club (renommés, masqués, créés) : lus par ensureParcours, un parcours masqué ne s'affiche plus.
// Tout se calcule en base (missions) ; les grades et les textes viennent de src/domain/badges.ts (testé).
import {
  badgeLabels, badgeSettings, badgesNewGrades, cheerGrade, clubAccounts, clubVolunteerNeeds, giveHeart,
  giveStar, gradeSeen, missionRanking, myBadges, saveBadgeSettings, saveGradeReward, setBadgePrefs, setNoShow,
  slotSignups, takeSlot, useGradeReward,
  type BadgeLabel, type MissionRank, type MyBadges, type NewGrade, type VolunteerNeed,
} from '../lib/api';
import {
  badgesLine, capText, cheerLine, earnedRewards, GRADES, gradeInfo, gradeOf, isParcours, MEDAL_LABEL, medalCounts, nextCap, parcoursOf,
  parcoursTotals, rewardFor, starParcours, toNextText, unitOf, volunteerTotal,
  type GradeKey, type GradeReward, type Medal, type ParcoursState,
} from '../domain/badges';
import { weekDay } from '../domain/volunteer';
import { addDays, localDate, parseLocalDate } from '../domain/schedule';
import { seasonOf } from '../domain/season';
import { PICTO_KEYS, PICTOS, pictoPath } from '../domain/pictos';
import { ensureParcours } from '../lib/reglages-api';
import { firstName, modOn, type Ctx } from './context';
import { $, $$, html, mount, openSheet, raw, toast, type Html } from './dom';
import { openUseHold, picto } from './perks';
import { needWhen, slotsChanged, slotsForMe } from './volunteer';
import { vouvoie } from './vous';
import { tourRunning } from '../guide/engine';
import { renderMyKm } from './carpool-km';
import { decoratePins } from './groupes';
import './badges.css';

const isAdmin = (ctx: Ctx) => !!ctx.club && ctx.adminOf.some(c => c.id === ctx.club!.id);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const shortDay = (d: string) => parseLocalDate(d.slice(0, 10)).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
const longDay = (d: string) => parseLocalDate(d.slice(0, 10)).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
/** Un compte partenaire seul (D-037) : vouvoiement (src/ui/vous.ts, D-103). */
const vousOf = vouvoie;

// -----------------------------------------------------------------------------
// Médaille, étoiles, pastille de grade (dessins constants de l'app : sûrs à insérer)
// -----------------------------------------------------------------------------
const STAR = 'M12 2.5l2.9 6.2 6.8.7-5.1 4.6 1.5 6.7L12 17.3l-6.1 3.4 1.5-6.7-5.1-4.6 6.8-.7z';
const svgPath = (d: string, s: number) =>
  `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;

/** La médaille d'un parcours : anneau de la couleur du grade, picto au centre. */
export function medal(p: string, k: GradeKey | 'off', size = 64): Html {
  return raw(`<span class="gr-medal g-${k}" style="--s:${size}px"><svg class="gr-m" viewBox="0 0 64 64" aria-hidden="true"><circle class="gr-ring" cx="32" cy="32" r="30"/><circle class="gr-core" cx="32" cy="32" r="23.5"/></svg><span class="gr-pic">${svgPath(pictoPath(parcoursOf(p).picto), Math.round(size * 0.4))}</span></span>`);
}
/** Cinq étoiles, `n` pleines. */
export function stars(n: number, k: GradeKey | 'off', size = 13): Html {
  return raw(`<span class="gr-stars g-${k}" role="img" aria-label="${n} étoile${n > 1 ? 's' : ''} sur 5">${[0, 1, 2, 3, 4].map(i =>
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" class="${i < n ? 'on' : ''}" aria-hidden="true"><path d="${STAR}"/></svg>`).join('')}</span>`);
}
export const gchip = (k: GradeKey) => html`<span class="gr-chip g-${k}">${gradeInfo(k).name}</span>`;

// -----------------------------------------------------------------------------
// Données : mes missions et le reste, en un appel ; la saison
// -----------------------------------------------------------------------------
export interface BadgeData { b: MyBadges; states: ParcoursState[]; started: ParcoursState[]; season: { from: string; to: string; name: string } | null }

export async function loadBadges(ctx: Ctx): Promise<BadgeData | null> {
  const club = ctx.club;
  if (!club || club.id.startsWith('host:')) return null;
  const [b] = await Promise.all([myBadges(club.id).catch(() => null), ensureParcours(club.id)]);
  if (!b) return null;
  // « Cette saison », les piliers : la saison FFBB, du 1er juillet au 30 juin (D-115), pas les dates d'activité du club.
  const season = seasonOf(localDate(new Date()));
  const states = parcoursTotals(b.missions, season);
  return { b, states, started: states.filter(x => x.missions > 0), season };
}

/** Une joueuse ou un joueur (une équipe) : sa page met l'Assiduité et le palmarès en avant (maquette 06). */
const isPlayer = (ctx: Ctx) => { const self = ctx.full.people.find(p => p.user_id === ctx.uid); return !!self && ctx.full.memberships.some(m => m.person_id === self.id); };

/** La tuile d'un parcours (Mes badges, À découvrir). */
function tile(s: ParcoursState): Html {
  const g = s.grade;
  if (g.key === 'off') {
    const hint = s.key === 'arbitrage' ? 'Un match d’école de basket pour commencer' : toNextText(s.key, 0);
    return html`<a class="gr-tile off" href="#/badges/${s.key}">${medal(s.key, 'off', 58)}<strong>${s.name}</strong><small class="muted">${hint}</small></a>`;
  }
  return html`<a class="gr-tile" href="#/badges/${s.key}">${medal(s.key, g.key, 58)}<strong>${s.name}</strong>
    <span class="gr-line">${gchip(g.key)}${stars(g.stars, g.key, 12)}</span><small class="muted">${toNextText(s.key, s.missions)}</small></a>`;
}

const defiMedal = (k: Medal, n: number) => html`<span class="gr-def g-${k}" aria-label="${n} ${MEDAL_LABEL[k].toLowerCase()}"><span class="gr-def-c">${picto('tournoi', 18)}</span><b>${n}</b></span>`;

// -----------------------------------------------------------------------------
// « Mes badges » et un parcours
// -----------------------------------------------------------------------------
export async function renderBadges(root: HTMLElement, ctx: Ctx, sub?: string): Promise<void> {
  if (!ctx.club) { mount(root, html`<div class="empty"><p class="empty-title">Aucun club.</p></div>`); return; }
  const d = await loadBadges(ctx);
  if (!d) { mount(root, html`<div class="empty"><p class="empty-title">Badges indisponibles pour le moment.</p></div>`); return; }
  // Un parcours masqué (D-127) ne s'affiche plus : son adresse mène à Mes badges.
  if (sub && isParcours(sub) && !parcoursOf(sub).hidden) return renderParcours(root, ctx, d, sub);
  const { b, states, started, season } = d;
  const vous = vousOf(ctx);
  const player = isPlayer(ctx);
  const name = firstName(ctx);
  const tot = volunteerTotal(b.missions, season);
  const ranking = season ? await missionRanking(ctx.club.id, season.from, season.to).catch(() => [] as MissionRank[]) : [];
  const rank = ranking.findIndex(r => r.me) + 1;
  const discover = starParcours().filter(k => !started.some(s => s.key === k)).map(k => states.find(s => s.key === k)!).filter(Boolean);
  const medals = medalCounts(b.palmares);
  const since = b.since ? b.since.slice(0, 4) : null;
  const reload = () => renderBadges(root, ctx, sub);

  mount(root, html`
    <section class="screen gr">
      <a class="back" href="#/moi">‹ Moi</a>
      <header class="screen-head"><p class="eyebrow">${name || 'Moi'}${since ? ` · au club depuis ${since}` : ''}</p><h2>${vous ? 'Vos badges' : 'Mes badges'}</h2>
        <p class="muted">${player ? 'Bénévolat, entraînements et défis : tout ce que tu as fait pour le club.'
          : vous ? 'Chaque mission fait monter son parcours. 5 étoiles = le grade suivant.' : 'Chaque mission tenue fait monter son parcours. 5 étoiles = le grade suivant.'}</p></header>
      ${player ? '' : html`
        <div class="gr-sum" data-guide="bg-sum">
          <div><b>${tot.all}</b><small>${tot.all > 1 ? 'missions' : 'mission'} au club</small></div>
          <div><b>${tot.season}</b><small>cette saison</small></div>
          <div><b>${rank ? `${rank}${rank === 1 ? 'er' : 'e'}` : '—'}</b><small>pilier de la saison</small></div>
        </div>`}
      <section class="block" data-guide="bg-parcours">
        <div class="block-head"><h3>${vous ? 'Vos parcours' : 'Mes parcours'}</h3></div>
        ${started.length ? html`<div class="gr-grid">${started.map(tile)}</div>`
          : html`<p class="muted">${vous ? 'Une entreprise présentée au club : votre première mission Ambassade.' : 'Ta première mission t’attend : un créneau tenu, et le parcours démarre.'}</p>`}
        ${started.some(s => s.key === 'assiduite') ? html`<p class="hint">Assiduité : une semaine où tu étais à tous les entraînements. Une absence médicale ou excusée ne retire rien.</p>` : ''}
      </section>
      ${b.palmares.length ? html`
        <section class="block" id="gr-palm">
          <div class="block-head"><h3>Palmarès des défis</h3></div>
          <div class="gr-palm">${(['or', 'argent', 'bronze'] as Medal[]).filter(k => medals[k]).map(k => defiMedal(k, medals[k]))}</div>
          <ul class="gr-hist">${b.palmares.slice(0, 8).map(m => html`
            <li><span class="muted small">${parseLocalDate(m.closed_at.slice(0, 10)).toLocaleDateString('fr-FR', { month: 'short' })}</span>
              <span><strong>${m.title}</strong> <span class="muted small">· ${fmtNum(m.value)}${m.unit ? ` ${m.unit}` : ''}</span></span>${gchip(m.medal)}</li>`)}</ul>
        </section>` : ''}
      ${player || !discover.length ? '' : html`
        <section class="block">
          <div class="block-head"><h3>À découvrir</h3></div>
          <div class="gr-grid">${discover.map(tile)}</div>
        </section>`}
      ${b.gifts.length ? html`
        <section class="block" id="gr-coeur">
          <div class="block-head"><h3>Coup de cœur du bureau</h3></div>
          ${b.gifts.map(g => html`<div class="gr-heart"><span class="gr-heart-ico">${picto(g.picto, 22)}</span>
            <div><strong>${g.name}</strong><span class="muted small">${g.by ? `Donné par ${g.by}` : 'Donné par le club'} · ${longDay(g.given_on)} ${g.given_on.slice(0, 4)}</span></div></div>`)}
        </section>` : ''}
      ${player ? '' : html`
        <section class="block">
          <div class="block-head"><h3>L’échelle, pour tous les parcours</h3></div>
          <ol class="gr-ladder-mini">${GRADES.map(g => html`<li>${gchip(g.key)}<span class="muted small">${g.key === 'herbe' ? 'dès la 1re mission' : `dès ${g.min} missions`}</span></li>`)}</ol>
          <p class="hint">Les étoiles ne se dépensent pas et ne baissent jamais. Elles ${vous ? 'vous' : 'te'} suivent d’une saison à l’autre.</p>
        </section>`}
      <div id="gr-km"></div>
      <section class="block gr-prefs">
        <label class="gr-toggle"><span><strong>Apparaître dans les piliers de la saison</strong><span class="muted small">Le classement du club, en missions.</span></span>
          <input type="checkbox" class="gr-sw" id="gr-rank" ${b.prefs.hide_ranking ? '' : 'checked'}></label>
        ${b.settings.bravo ? html`<label class="gr-toggle"><span><strong>Apparaître dans l’annonce « Bravo à… »</strong><span class="muted small">Chaque lundi, les nouveaux grades de la semaine.</span></span>
          <input type="checkbox" class="gr-sw" id="gr-bravo" ${b.prefs.hide_bravo ? '' : 'checked'}></label>` : ''}
      </section>
    </section>`);

  const km = $(root, '#gr-km')!;
  void renderMyKm(km, ctx, { hideEmpty: true }).then(() => { if (!km.childElementCount) km.remove(); });   // mes km de conducteur (D-069)
  $<HTMLInputElement>(root, '#gr-rank')?.addEventListener('change', async (e) => {
    const show = (e.target as HTMLInputElement).checked;
    try { await setBadgePrefs({ hide_ranking: !show }); toast(vous ? (show ? 'Vous apparaissez dans les piliers' : 'Vous n’apparaissez plus dans les piliers') : show ? 'Tu apparais dans les piliers' : 'Tu n’apparais plus dans les piliers'); }
    catch { toast('Impossible pour le moment.', 'ko'); await reload(); }
  });
  $<HTMLInputElement>(root, '#gr-bravo')?.addEventListener('change', async (e) => {
    const show = (e.target as HTMLInputElement).checked;
    try { await setBadgePrefs({ hide_bravo: !show }); toast(vous ? (show ? 'Votre grade sera cité le lundi' : 'Vous n’apparaissez plus dans l’annonce') : show ? 'Ton grade sera cité le lundi' : 'Tu n’apparais plus dans l’annonce'); }
    catch { toast('Impossible pour le moment.', 'ko'); await reload(); }
  });
}

const fmtNum = (v: number) => String(Math.round(Number(v) * 100) / 100).replace('.', ',');

/** Ce qui compte dans un parcours (maquette 03). */
function countsHint(p: string, vous: boolean): string {
  switch (p) {
    case 'assiduite': return `Une semaine à tous les entraînements de ${vous ? 'votre' : 'ton'} équipe = 1 mission, d’après l’appel du coach. Une absence médicale ou excusée ne retire rien.`;
    case 'ambassade': return 'Une recrue qui tient son premier créneau = 1 mission ; une entreprise présentée au club = 1 ; un partenaire signé = 3.';
    case 'encadrement': return 'Un créneau tenu = 1 mission, comptée le lendemain. Parrainage des jeunes : chaque semaine avec un encouragement = 1 mission.';
    case 'coup_de_main': return 'Un créneau dont le nom n’entre dans aucun parcours, ou une étoile du bureau.';
    default:
      // Un parcours créé par le club (D-127) : sa phrase, puis la règle commune.
      if (parcoursOf(p).custom) return `${parcoursOf(p).blurb ? `${parcoursOf(p).blurb.replace(/[.!]\s*$/, '')}. ` : ''}Un créneau tenu = 1 mission, comptée le lendemain.`;
      return 'Un créneau tenu = 1 mission, comptée le lendemain. Le chef de groupe peut retirer une absence pendant 48 h.';
  }
}

async function renderParcours(root: HTMLElement, ctx: Ctx, d: BadgeData, key: string): Promise<void> {
  const club = ctx.club!;
  const vous = vousOf(ctx);
  const s = d.states.find(x => x.key === key)!;
  const g = s.grade;
  const labels = parcoursOf(s.key).slot ? (await badgeLabels(club.id).catch(() => [] as BadgeLabel[])).filter(l => l.parcours === key) : [];
  const rewards = d.b.rewards;
  const uses = d.b.uses.filter(u => u.parcours === key);
  const rows = d.b.missions.filter(m => m.parcours === key).slice(0, 6);
  const unit = (n: number) => unitOf(key, n);
  const reload = () => renderBadges(root, ctx, key);
  const step = (gr: typeof GRADES[number]) => {
    const done = s.missions >= gr.min;
    const r = rewards.find(x => x.grade === gr.key);
    // D-122 : les récompenses des grades viennent avec le module Avantages (jamais promises sans lui).
    const showR = modOn(ctx, 'avantages') && r && rewardFor(r, key) && (r.perk || gr.key !== 'herbe');
    const used = uses.find(u => u.grade === gr.key);
    const state = !showR || !r!.perk ? '' : used ? 'used' : done ? 'cur' : '';
    return html`<li class="gr-step g-${gr.key} ${done ? 'done' : ''}">
      <span class="gr-dot">${done ? '✓' : ''}</span>
      <div class="gr-step-main"><span class="gr-step-h">${gchip(gr.key)}<span class="muted small">${gr.key === 'herbe' ? (key === 'assiduite' ? '1re semaine' : '1re mission') : `${gr.min} ${unit(gr.min)}`}</span></span>
        ${showR ? html`<span class="gr-reward ${state}">${picto(r!.picto, 16)}<span>${r!.title}</span>${
          state === 'used' ? html`<span class="muted small">utilisée le ${longDay(used!.used_at)}</span>`
          : state === 'cur' ? html`<button type="button" class="btn btn-primary btn-sm" data-use="${gr.key}">Utiliser</button>` : ''}</span>` : ''}
      </div></li>`;
  };
  const foot = g.key === 'off' ? [toNextText(key, 0), '']
    : !g.next ? ['Grade maximum : merci !', '']
    : [`${g.toStar} ${unit(g.toStar)} avant la prochaine étoile`, `${g.toNext} avant ${g.next.name}`];

  mount(root, html`
    <section class="screen gr">
      <a class="back" href="#/badges">‹ Mes badges</a>
      <header class="screen-head"><p class="eyebrow">Mes badges</p><h2>${s.name}</h2></header>
      <article class="gr-big g-${g.key}" data-guide="bg-big">
        ${medal(key, g.key, 96)}
        <div class="gr-big-txt"><span class="gr-big-g">${g.key === 'off' ? 'À découvrir' : g.name}</span>${stars(g.stars, g.key, 18)}
          <span class="gr-big-n"><b>${s.missions}</b> ${unit(s.missions)}</span></div>
        <div class="gr-bar" role="img" aria-label="${g.pct} % vers le grade suivant"><span style="width:${g.pct}%"></span></div>
        <p class="gr-big-foot"><span>${foot[0]}</span><span>${foot[1]}</span></p>
      </article>
      <section class="block" data-guide="bg-ladder">
        <div class="block-head"><h3>Grades et récompenses</h3></div>
        <ol class="gr-ladder">${GRADES.map(step)}</ol>
      </section>
      <section class="block" id="gr-counts" data-guide="bg-counts">
        <div class="block-head"><h3>Ce qui compte</h3></div>
        ${labels.length ? html`<p class="gr-labels">${labels.slice(0, 10).map(l => html`<span class="chip chip-mute">${l.label}</span>`)}</p>` : ''}
        <p class="hint">${countsHint(key, vous)}</p>
      </section>
      ${rows.length ? html`
        <section class="block">
          <div class="block-head"><h3>Dernières missions</h3></div>
          <ul class="gr-hist">${rows.map(m => html`
            <li><span class="muted small">${shortDay(m.day)}</span><span><strong>${m.source === 'assiduite' ? 'Semaine complète' : m.label}</strong>${m.title ? html` <span class="muted small">· ${m.title}</span>` : ''}</span><b>+${m.missions}</b></li>`)}</ul>
        </section>` : ''}
    </section>`);

  for (const b of $$<HTMLButtonElement>(root, '[data-use]')) {
    b.addEventListener('click', () => {
      const r = rewards.find(x => x.grade === b.dataset.use)!;
      openUseHold(ctx, { title: r.title, picto: r.picto, details: `Pour ${vous ? 'votre' : 'ton'} grade ${gradeInfo(r.grade).name} en ${s.name}.` },
        () => useGradeReward(club.id, r.grade, key), () => void reload());
    });
  }
}

// -----------------------------------------------------------------------------
// Accueil : le prochain cap, la bande « Mes badges » (maquette 01)
// -----------------------------------------------------------------------------
/** Les créneaux libres que je peux prendre (2 mois), sans ceux en conflit avec ce qui m'occupe (D-103), et le
 *  parcours de chaque nom de créneau. */
async function openSlots(ctx: Ctx): Promise<{ open: VolunteerNeed[]; labels: Map<string, string> }> {
  const club = ctx.club!.id;
  const today = localDate(new Date());
  const [mine, labels] = await Promise.all([slotsForMe(ctx, 60), badgeLabels(club).catch(() => [] as BadgeLabel[])]);
  const open = mine.open.filter(n => n.occurrence_date > today || (n.starts_at && new Date(n.starts_at).getTime() > Date.now()));
  return { open, labels: new Map(labels.map(l => [l.label_key, l.parcours])) };
}

const WHERE: Record<string, string> = { table: 'à la table', buvette: 'à la buvette', arbitrage: 'à l’arbitrage', logistique: 'à l’installation', transport: 'pour conduire' };

/** « Ton prochain cap » (sauf `cap: false`) et, si `strip`, la bande « Mes badges » (accueil Bénévole ; carte de
 *  bénévolat des jeunes ; page Bénévolat des membres). */
export async function renderBadgesHome(el: HTMLElement, ctx: Ctx, opts: { strip?: boolean; cap?: boolean } = {}): Promise<void> {
  if (!ctx.club) return;
  const wantCap = opts.cap !== false;
  const none = { open: [] as VolunteerNeed[], labels: new Map<string, string>() };
  const [d, slots] = await Promise.all([loadBadges(ctx), wantCap ? openSlots(ctx).catch(() => none) : Promise.resolve(none)]);
  if (!d) { mount(el, html``); return; }
  const c = wantCap ? nextCap(d.states, slots.open, slots.labels) : null;
  const today = localDate(new Date());
  const name = firstName(ctx);
  const reload = () => renderBadgesHome(el, ctx, opts);
  const capCard = c ? (() => {
    const p = c.parcours, g = p.grade;
    const target = g.next ? `${p.name} · ${g.next.name}` : p.name;
    const sl = c.slot;
    const left = sl ? sl.needed - sl.taken : 0;
    const dayWord = sl ? weekDay(sl.occurrence_date, today) : '';
    const when = sl ? (Date.parse(`${sl.occurrence_date}T12:00:00`) - Date.parse(`${today}T12:00:00`) < 7 * 86_400_000
      ? (dayWord === 'aujourd’hui' || dayWord === 'demain' ? dayWord : `ce ${dayWord}`) : `le ${longDay(sl.occurrence_date)}`) : '';
    return html`
      <article class="block gr-cap" data-guide="home-cap">
        <div class="gr-cap-top">
          ${medal(p.key, g.key, 52)}
          <div class="gr-cap-txt"><p class="eyebrow">${vousOf(ctx) ? 'Votre prochain cap' : 'Ton prochain cap'}</p>
            <strong>${target}</strong>
            ${g.key === 'off' ? html`<span class="muted small">Une mission pour commencer</span>`
              : html`<span class="gr-line">${gchip(g.key as GradeKey)}${stars(g.stars, g.key, 12)}<span class="muted small">${capText(p.key, p.missions)}</span></span>`}</div>
        </div>
        ${sl ? html`
          <div class="gr-slot"><div><strong>${sl.label} · ${sl.title}</strong><span class="muted small">${cap(parseLocalDate(sl.occurrence_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' }))} · ${needWhen(sl)}${sl.location ? ` · ${sl.location}` : ''}</span></div>
            <button type="button" class="btn btn-primary btn-sm" data-cap-take="${sl.slot_id}">Je prends</button></div>
          <p class="hint">Proposé parce qu’il reste ${left > 1 ? `${left} places` : 'une place'} ${WHERE[p.key] ?? 'sur ce créneau'} ${when}.</p>` : ''}
      </article>`;
  })() : '';
  const strip = opts.strip && d.started.length ? html`
    <a class="card card-row gr-mine" href="#/badges" data-guide="home-badges">
      <span class="gr-mini">${d.started.slice(0, 3).map(s => medal(s.key, s.grade.key, 34))}</span>
      <span class="card-main"><strong>${vousOf(ctx) ? 'Vos badges' : 'Mes badges'}</strong><span class="muted small">${badgesLine(d.started)}</span></span>
      <span class="occ-go" aria-hidden="true">›</span></a>` : '';
  mount(el, html`${capCard}${strip}`);
  $<HTMLButtonElement>(el, '[data-cap-take]')?.addEventListener('click', async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    b.disabled = true;
    try { await takeSlot(b.dataset.capTake!, name || 'Bénévole'); slotsChanged(); toast('Merci ! Ton créneau est noté'); await reload(); }
    catch (err) { b.disabled = false; toast(String((err as Error).message).includes('complet') ? 'Ce créneau vient d’être pris.' : 'Inscription impossible.', 'ko'); }
  });
}

// -----------------------------------------------------------------------------
// Moi : la carte « Mes badges » (maquette m3), et le compte de mes récompenses à utiliser
// -----------------------------------------------------------------------------
export async function renderBadgesMoiCard(el: HTMLElement, ctx: Ctx, d?: BadgeData | null): Promise<void> {
  const data = d === undefined ? await loadBadges(ctx) : d;
  if (!data) { mount(el, html``); return; }
  const fresh = data.b.ups.some(u => !u.seen || Date.parse(u.reached_on) >= Date.now() - 7 * 86_400_000);
  const n = data.b.palmares.length;
  const line = [badgesLine(data.started), n ? `palmarès : ${n} ${n > 1 ? 'médailles' : 'médaille'}` : ''].filter(Boolean).join(' · ');
  const vous = vousOf(ctx);
  mount(el, html`
    <a class="card gr-moi" href="#/badges" data-guide="me-badges">
      <span class="gr-moi-top"><span class="eyebrow">${vous ? 'Vos badges' : 'Mes badges'}</span>${fresh ? html`<span class="chip chip-hot">Nouveau grade</span>` : ''}</span>
      ${data.started.length ? html`<span class="gr-moi-row">${data.started.slice(0, 4).map(s => medal(s.key, s.grade.key, 50))}</span>` : ''}
      <span class="muted small">${line || (vous ? 'Une entreprise présentée au club : votre première mission Ambassade.' : 'Un créneau tenu = une mission : ton premier badge t’attend.')}</span>
    </a>`);
}

/** Mes récompenses de grade à utiliser (pour Moi et Avantages). */
export const rewardsToUse = (d: BadgeData) => earnedRewards(d.states, d.b.rewards, d.b.uses).filter(r => !r.used_at);

// -----------------------------------------------------------------------------
// La fête du passage de grade (maquette 04), une fois, à l'ouverture
// -----------------------------------------------------------------------------
let partyShown = false;
export async function showGradeUps(ctx: Ctx): Promise<void> {
  if (partyShown || !ctx.club || ctx.club.id.startsWith('host:') || tourRunning()) return;
  partyShown = true;
  const d = await loadBadges(ctx);
  if (!d || tourRunning()) return;
  const queue = d.b.ups.filter(u => !u.seen).sort((a, b) => gradeInfo(b.grade).min - gradeInfo(a.grade).min);
  const next = () => { const u = queue.shift(); if (u) party(ctx, d, u.parcours, u.grade, next); };
  next();
}

function party(ctx: Ctx, d: BadgeData, p: string, k: GradeKey, onDone: () => void): void {
  const club = ctx.club!;
  const s = d.states.find(x => x.key === p);
  const g = gradeOf(s?.missions ?? gradeInfo(k).min);
  const vous = vousOf(ctx);
  const r = d.b.rewards.find(x => x.grade === k);
  const gift = modOn(ctx, 'avantages') && r && r.perk && rewardFor(r, p) && !d.b.uses.some(u => u.grade === k && u.parcours === p) ? r : null;
  const host = document.createElement('div');
  host.className = `gr-party g-${k}`;
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-modal', 'true');
  host.setAttribute('aria-label', `Nouveau grade : ${parcoursOf(p).name} ${gradeInfo(k).name}`);
  const colors = ['#F2B84B', '#FFFFFF', '#E3A469', '#7FD1A8'];
  mount(host, html`
    <button type="button" class="sheet-backdrop" data-close hidden aria-hidden="true"></button>
    <div class="gr-confetti" aria-hidden="true">${raw(Array.from({ length: 26 }, (_, i) =>
      `<i style="--x:${(i * 37) % 100}%;--y:${(i * 53) % 46}%;--r:${(i * 47) % 180}deg;--c:${colors[i % 4]};--d:${(i * 71) % 600}"></i>`).join(''))}</div>
    <div class="gr-party-in">
      <p class="gr-party-eye">Nouveau grade</p>
      ${medal(p, k, 132)}
      <h2 class="gr-party-title">${parcoursOf(p).name}<br>${gradeInfo(k).name}</h2>
      ${stars(g.key === k ? g.stars : 0, k, 22)}
      <p class="gr-party-txt">${cheerLine(p, s?.missions ?? gradeInfo(k).min, firstName(ctx), vous)}</p>
      ${gift ? html`
        <div class="gr-party-gift">
          <span class="pk-ico">${picto(gift.picto, 24)}</span>
          <div><span class="gr-party-gift-eye">${vous ? 'Votre' : 'Ta'} récompense</span><strong>${gift.title}</strong><span class="small">Une fois, quand ${vous ? 'vous voulez' : 'tu veux'}</span></div>
        </div>
        <button type="button" class="btn gr-party-btn" id="gr-now">Utiliser maintenant</button>
        <button type="button" class="gr-party-later" id="gr-later">Plus tard, elle ${vous ? 'vous' : 't’'}attend dans Avantages</button>`
      : html`<button type="button" class="btn gr-party-btn" id="gr-later">Merci !</button>`}
      ${d.b.settings.bravo && !d.b.prefs.hide_bravo && !d.b.prefs.hide_ranking ? html`
        <p class="gr-party-small">Lundi, l’annonce « Bravo à… » citera ${vous ? 'votre' : 'ton'} grade. <button type="button" id="gr-hide">Ne pas y apparaître</button></p>` : ''}
    </div>`);
  document.body.append(host);
  const close = (after?: () => void) => {
    host.remove();
    void gradeSeen(club.id, p).catch(() => undefined);
    if (after) after(); else onDone();
  };
  $(host, '[data-close]')!.addEventListener('click', () => close());
  $(host, '#gr-later')?.addEventListener('click', () => close());
  $(host, '#gr-now')?.addEventListener('click', () => close(() =>
    openUseHold(ctx, { title: gift!.title, picto: gift!.picto, details: `Pour ${vous ? 'votre' : 'ton'} grade ${gradeInfo(k).name} en ${parcoursOf(p).name}.` },
      () => useGradeReward(club.id, k, p), onDone)));
  $(host, '#gr-hide')?.addEventListener('click', async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    try { await setBadgePrefs({ hide_bravo: true }); b.closest('p')!.textContent = 'C’est noté : pas dans l’annonce du lundi.'; }
    catch { toast('Impossible pour le moment.', 'ko'); }
  });
  ($(host, '#gr-now') ?? $(host, '#gr-later'))?.focus();
}

// -----------------------------------------------------------------------------
// Bureau : Bénévolat › Badges (maquette 07)
// -----------------------------------------------------------------------------
export async function renderBadgesBoard(el: HTMLElement, ctx: Ctx): Promise<void> {
  const club = ctx.club!;
  const today = localDate(new Date());
  const season = seasonOf(today);   // les piliers de la saison FFBB (D-115)
  const [news, settings, ranking, past] = await Promise.all([
    ensureParcours(club.id).then(() => badgesNewGrades(club.id)).catch(() => [] as NewGrade[]),
    badgeSettings(club.id).catch(() => null),
    missionRanking(club.id, season.from, season.to).catch(() => [] as MissionRank[]),
    clubVolunteerNeeds(club.id, addDays(today, -14), addDays(today, -1)).catch(() => [] as VolunteerNeed[]),
  ]);
  const signups = await slotSignups(past.filter(n => n.taken).map(n => n.slot_id)).catch(() => []);
  const bySlot = new Map(past.map(n => [n.slot_id, n]));
  const reload = () => renderBadgesBoard(el, ctx);

  mount(el, html`
    <section class="block" data-guide="bg-news">
      <div class="block-head"><h3>Nouveaux grades · 7 jours</h3></div>
      ${news.length ? html`<ul class="gr-news">${news.map(n => html`
        <li>${medal(n.parcours, n.grade, 40)}<div><strong>${n.name}</strong><span class="muted small">${parcoursOf(n.parcours).name} · ${gradeInfo(n.grade).name}</span></div>
          ${n.cheered ? html`<span class="chip chip-soft">Félicité ✓</span>`
            : html`<button type="button" class="btn btn-ghost btn-sm" data-cheer="${n.user_id}|${n.parcours}|${n.grade}">Féliciter</button>`}</li>`)}</ul>`
        : html`<p class="muted">Pas de nouveau grade cette semaine. Ils arrivent tout seuls avec les créneaux tenus.</p>`}
      <label class="gr-toggle"><span><strong>Annonce « Bravo à… » chaque lundi</strong><span class="muted small">Les nouveaux grades de la semaine, envoyés tout seuls. Chacun peut ne pas y apparaître.</span></span>
        <input type="checkbox" class="gr-sw" id="gr-bravo-club" ${settings?.badges_bravo === false ? '' : 'checked'}></label>
    </section>
    <section class="block" id="gr-piliers">
      <div class="block-head"><h3>Les piliers de la saison</h3><button type="button" class="btn btn-ghost btn-sm" id="gr-star" data-guide="bg-star">Donner une étoile</button></div>
      ${ranking.length ? html`<ol class="vp-rank">${ranking.slice(0, 8).map((r, i) => html`
        <li><b>${i + 1}</b><span data-pin-user="${r.user_id}">${r.name}</span><small>${r.parcours} parcours</small><b>${r.missions}</b></li>`)}</ol>`
        : html`<p class="muted">Les missions arrivent le lendemain des créneaux tenus.</p>`}
      <p class="hint">En missions tenues cette saison. Chacun peut s’en retirer depuis Mes badges.</p>
    </section>
    <button type="button" class="card card-row gr-heart-row" id="gr-heart"><span class="gr-heart-ico">${picto('coeur', 22)}</span>
      <span class="card-main"><strong>Un badge coup de cœur</strong><span class="muted small">Donné à la main : « Chef des crêpes du loto »</span></span><span class="occ-go" aria-hidden="true">›</span></button>
    ${signups.length ? html`
      <details class="alt-box">
        <summary>Présences des 15 derniers jours · ${signups.length}</summary>
        <p class="hint">Par défaut, chacun est venu. Un absent ne compte pas de mission ; le chef d’un groupe peut aussi le noter pendant 48 h.</p>
        <ul class="ce-rows">${signups.map(s => {
          const n = bySlot.get(s.slot_id);
          return html`<li><span><strong>${s.display_name}</strong><br><span class="muted small">${n ? `${n.label} · ${n.title} · ${shortDay(n.occurrence_date)}` : ''}</span></span>
            <span class="st2 st2-sm" role="group" aria-label="Présence de ${s.display_name}">
              <button type="button" class="${s.no_show ? '' : 'on ok'}" data-ns="${s.slot_id}|${s.user_id}" data-v="" aria-pressed="${String(!s.no_show)}">${s.no_show ? '' : '✓ '}Venu</button>
              <button type="button" class="${s.no_show ? 'on ko' : ''}" data-ns="${s.slot_id}|${s.user_id}" data-v="1" aria-pressed="${String(!!s.no_show)}">${s.no_show ? '✕ ' : ''}Absent</button></span></li>`;
        })}</ul>
      </details>` : ''}`);

  void decoratePins(el, club.id);   // la pastille du groupe à côté des piliers (D-105)
  for (const b of $$<HTMLButtonElement>(el, '[data-cheer]')) {
    b.addEventListener('click', async () => {
      const [user, p, g] = b.dataset.cheer!.split('|');
      b.disabled = true;
      try { await cheerGrade(club.id, user!, p!, g!); toast('Félicitations envoyées'); await reload(); }
      catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  }
  $<HTMLInputElement>(el, '#gr-bravo-club')?.addEventListener('change', async (e) => {
    const on = (e.target as HTMLInputElement).checked;
    try { await saveBadgeSettings(club.id, { badges_bravo: on }); toast(on ? 'L’annonce partira lundi' : 'Plus d’annonce le lundi'); }
    catch { toast('Impossible pour le moment.', 'ko'); await reload(); }
  });
  $(el, '#gr-star')!.addEventListener('click', () => void openGiveStar(ctx, () => void reload()));
  $(el, '#gr-heart')!.addEventListener('click', () => void openGiveHeart(ctx));
  for (const b of $$<HTMLButtonElement>(el, '[data-ns]')) {
    b.addEventListener('click', async () => {
      const [slot, user] = b.dataset.ns!.split('|');
      if (b.getAttribute('aria-pressed') === 'true') return;
      b.disabled = true;
      try { await setNoShow(slot!, user!, !!b.dataset.v); toast(b.dataset.v ? 'Noté absent : pas de mission' : 'Noté présent'); await reload(); }
      catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  }
}

/** « Donner une étoile » (maquette 09) : pour qui, le parcours, pour quoi. */
export async function openGiveStar(ctx: Ctx, onDone: () => void): Promise<void> {
  const club = ctx.club!;
  const people = await clubAccounts(club.id).catch(() => []);
  const choices = starParcours();
  let pick = choices[0]!;
  openSheet('Donner une étoile', html`
    <form class="form" id="f-star" novalidate>
      <label class="field"><span>Pour qui</span><select name="user">${people.map(p => html`<option value="${p.user_id}">${p.name}</option>`)}</select></label>
      <div class="field"><span>Parcours</span><div class="gr-pick" role="radiogroup" aria-label="Parcours">${choices.map((k, i) => html`
        <button type="button" role="radio" class="gr-pick-b" data-p="${k}" aria-pressed="${String(i === 0)}">${picto(parcoursOf(k).picto, 20)}<span>${parcoursOf(k).name}</span></button>`)}</div></div>
      <label class="field"><span>Pour quoi</span><input name="reason" maxlength="120" placeholder="ex. Arbitrage du plateau U9 à Torvigne"></label>
      <p class="hint">Compte comme 1 mission dans le parcours, avec ce motif dans son historique.</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Donner l’étoile</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-star')!;
    for (const b of $$<HTMLButtonElement>(f, '[data-p]')) b.addEventListener('click', () => {
      pick = b.dataset.p as typeof pick;
      $$(f, '[data-p]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const reason = String(d.get('reason') ?? '').trim();
      if (!reason) { $(f, '.form-msg')!.textContent = 'Dis pour quoi : la personne le lit dans son historique.'; return; }
      const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
      btn.disabled = true;
      try { await giveStar(club.id, String(d.get('user')), pick, reason); close(); toast('Étoile donnée'); onDone(); }
      catch { btn.disabled = false; $(f, '.form-msg')!.textContent = 'Impossible pour le moment.'; }
    });
  });
}

/** Un badge « coup de cœur », donné à la main. */
async function openGiveHeart(ctx: Ctx): Promise<void> {
  const club = ctx.club!;
  const people = await clubAccounts(club.id).catch(() => []);
  let pic = 'coeur';
  const choices = ['coeur', 'etoile', 'gateaux', 'buvette', 'tournoi', 'photo', 'accueil', 'communication', 'installation', 'vip'];
  openSheet('Un badge coup de cœur', html`
    <form class="form" id="f-heart" novalidate>
      <label class="field"><span>Pour qui</span><select name="user">${people.map(p => html`<option value="${p.user_id}">${p.name}</option>`)}</select></label>
      <label class="field"><span>Le badge</span><input name="name" maxlength="60" placeholder="ex. Chef des crêpes du loto"></label>
      <div class="field"><span>Picto</span><div class="pk-pictos" role="radiogroup" aria-label="Picto">${choices.map(k => html`
        <button type="button" role="radio" class="pk-pic" data-pic="${k}" aria-pressed="${String(k === pic)}" aria-label="${PICTOS[k]!.label}" title="${PICTOS[k]!.label}">${picto(k, 22)}</button>`)}</div></div>
      <p class="hint">Il s’affiche dans ses badges, « Coup de cœur du bureau ». La personne est prévenue.</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Donner le badge</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-heart')!;
    for (const b of $$<HTMLButtonElement>(f, '[data-pic]')) b.addEventListener('click', () => {
      pic = b.dataset.pic!; $$(f, '[data-pic]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const name = String(d.get('name') ?? '').trim();
      if (!name) { $(f, '.form-msg')!.textContent = 'Donne un nom au badge.'; return; }
      try { await giveHeart(club.id, String(d.get('user')), name, pic); close(); toast('Badge donné'); }
      catch { $(f, '.form-msg')!.textContent = 'Impossible pour le moment.'; }
    });
  });
}

// -----------------------------------------------------------------------------
// Bureau : la récompense de chaque grade (maquette 08) ; l'écran est dans Club › Bénévolat (D-127, src/ui/reglages.ts)
// -----------------------------------------------------------------------------
const REWARD_PICTOS = ['coeur', 'cafe', 'cadeau', 'vip', 'maillot', 'glace', 'ticket', 'reduction', 'etoile', 'buvette', 'tournoi', 'gateaux'];

/** « les joueuses », « les joueurs », « les joueuses et les joueurs » selon les équipes du club (D-008 : pas d'écriture inclusive). */
export function playersWord(ctx: Ctx): string {
  const gs = new Set(ctx.full.teams.filter(t => t.club_id === ctx.club?.id).map(t => t.gender));
  return gs.size === 1 && gs.has('F') ? 'les joueuses' : gs.size === 1 && gs.has('M') ? 'les joueurs' : 'les joueuses et les joueurs';
}

/** Régler la récompense d'un grade : picto, texte, cadeau à utiliser ou simple merci, parcours. */
export function openReward(ctx: Ctx, r: GradeReward | null, grade: GradeKey, onDone: () => void): void {
  const club = ctx.club!;
  let pic = r?.picto ?? 'cadeau';
  const chosen = new Set(r?.parcours ?? []);
  openSheet(`Récompense · ${gradeInfo(grade).name}`, html`
    <form class="form" id="f-rw" novalidate>
      <label class="field"><span>La récompense</span><input name="title" maxlength="80" value="${r?.title ?? ''}" placeholder="ex. Boisson offerte à la buvette"></label>
      <div class="field"><span>Picto</span><div class="pk-pictos" role="radiogroup" aria-label="Picto">${REWARD_PICTOS.map(k => html`
        <button type="button" role="radio" class="pk-pic" data-pic="${k}" aria-pressed="${String(k === pic)}" aria-label="${PICTOS[k]!.label}" title="${PICTOS[k]!.label}">${picto(k, 22)}</button>`)}</div></div>
      <label class="check"><input type="checkbox" name="merci" ${r && !r.perk ? 'checked' : ''}><span><strong>Juste un merci dans l’app</strong><br><span class="muted small">Rien à utiliser chez un commerçant.</span></span></label>
      <div class="field"><span>Dans quels parcours</span><div class="gr-labels" id="rw-pcs">${[...starParcours(), 'assiduite'].map(k => html`
        <button type="button" class="chip ${chosen.has(k) ? 'chip-accent' : 'chip-mute'}" data-pc="${k}" aria-pressed="${String(chosen.has(k))}">${parcoursOf(k).name}</button>`)}</div>
        <p class="hint">Aucun choisi : tous les parcours.</p></div>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-rw')!;
    for (const b of $$<HTMLButtonElement>(f, '[data-pic]')) b.addEventListener('click', () => {
      pic = b.dataset.pic!; $$(f, '[data-pic]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    });
    for (const b of $$<HTMLButtonElement>(f, '[data-pc]')) b.addEventListener('click', () => {
      const k = b.dataset.pc!;
      if (chosen.has(k)) chosen.delete(k); else chosen.add(k);
      b.setAttribute('aria-pressed', String(chosen.has(k)));
      b.className = `chip ${chosen.has(k) ? 'chip-accent' : 'chip-mute'}`;
    });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const title = String(d.get('title') ?? '').trim();
      if (!title) { $(f, '.form-msg')!.textContent = 'Écris la récompense.'; return; }
      try {
        await saveGradeReward(club.id, { grade, picto: pic, title, perk: d.get('merci') !== 'on', parcours: [...chosen] });
        close(); toast('Récompense enregistrée'); onDone();
      } catch { $(f, '.form-msg')!.textContent = 'Enregistrement impossible.'; }
    });
  });
}
